import { Margin, usePDF } from "react-to-pdf";
import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { axiosInstance } from "../../../../utils/networking";

const PRIMARY = "#9D2362";
const CARD_BORDER = "1px solid #ececf0";

// Rotating palette for the assistance-category segments.
const CATEGORY_COLORS = [
  PRIMARY,
  "#3F51B5",
  "#00897b",
  "#fdb814",
  "#c2185b",
  "#5c6bc0",
  "#26a69a",
  "#ef6c00",
  "#7e57c2",
  "#43a047",
  "#8d6e63",
  "#9e9e9e",
];

// ---- Small presentational helpers -------------------------------------------

const SectionCard = ({ children, sx }: { children: any; sx?: any }) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      borderRadius: "16px",
      border: CARD_BORDER,
      backgroundColor: "white",
      ...sx,
    }}
  >
    {children}
  </Paper>
);

const SectionTitle = ({
  children,
  subtitle,
}: {
  children: any;
  subtitle?: string;
}) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      variant="h6"
      sx={{
        fontWeight: 700,
        color: "#2c2c34",
        borderLeft: `4px solid ${PRIMARY}`,
        pl: 1.5,
        lineHeight: 1.25,
      }}
    >
      {children}
    </Typography>
    {subtitle ? (
      <Typography variant="body2" sx={{ color: "#8a8a93", pl: 1.5, mt: 0.5 }}>
        {subtitle}
      </Typography>
    ) : null}
  </Box>
);

const StatCard = ({
  label,
  value,
  accent,
}: {
  label: string;
  value: any;
  accent: string;
}) => (
  <Paper
    elevation={0}
    sx={{
      flex: "1 1 180px",
      p: 2.5,
      borderRadius: "16px",
      border: CARD_BORDER,
      borderTop: `3px solid ${accent}`,
      backgroundColor: "white",
      textAlign: "center",
    }}
  >
    <Typography
      sx={{
        fontSize: "2rem",
        fontWeight: 800,
        color: "#2c2c34",
        lineHeight: 1.1,
      }}
    >
      {value}
    </Typography>
    <Typography
      variant="caption"
      sx={{
        color: "#8a8a93",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        fontWeight: 600,
      }}
    >
      {label}
    </Typography>
  </Paper>
);

const ChartCard = ({
  title,
  subtitle,
  height,
  children,
}: {
  title: string;
  subtitle?: string;
  height: number | string;
  children: any;
}) => (
  <SectionCard sx={{ flex: "1 1 380px", minWidth: 0 }}>
    <SectionTitle subtitle={subtitle}>{title}</SectionTitle>
    <Box sx={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        {children}
      </ResponsiveContainer>
    </Box>
  </SectionCard>
);

const GeneralClientReport = () => {
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);

  const fetchReport = (start: Dayjs | null, end: Dayjs | null) => {
    setLoading(true);
    let url = `/reports/general_client`;
    const params: string[] = [];
    if (start) params.push(`startDate=${start.format("YYYY-MM-DD")}`);
    if (end) params.push(`endDate=${end.format("YYYY-MM-DD")}`);
    if (params.length > 0) url = url + `?` + params.join("&");
    axiosInstance
      .get(url)
      .then((response: any) => {
        setReport(response.data);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchReport(null, null);
  }, []);

  const { toPDF, targetRef } = usePDF({
    filename: "General_Client_Report.pdf",
    page: { margin: Margin.SMALL, format: "letter" },
    overrides: {
      canvas: {
        removeContainer: false,
      },
    },
  });

  const handleCsvDownload = () => {
    if (!report) return;
    const csvContent: any[] = [];
    csvContent.push(["Category of Assistance", "Referrals", "Percentage"]);
    for (const row of report.categories) {
      csvContent.push([
        row.category,
        row.count,
        `${Math.round((row.percentage || 0) * 100)}%`,
      ]);
    }

    let csvString = ``;
    for (const row of csvContent) {
      let rowString = `\n`;
      for (const item of row) {
        rowString = rowString + `"${item}",`;
      }
      csvString = csvString + rowString;
    }

    const blob = new Blob([csvString], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `general_client_report.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    axiosInstance.post("/export_log", {
      export_type: "general_client_report",
      record_count: report.categories.length,
      filters: {
        report: "GeneralClient",
        startDate: startDate ? startDate.format("YYYY-MM-DD") : null,
        endDate: endDate ? endDate.format("YYYY-MM-DD") : null,
      },
    });
  };

  const rangeLabel =
    startDate || endDate
      ? `${startDate ? startDate.format("MMM D, YYYY") : "Beginning"} – ${
          endDate ? endDate.format("MMM D, YYYY") : "Today"
        }`
      : "All time";

  const toolbar = (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 3,
        borderRadius: "16px",
        border: CARD_BORDER,
        display: "flex",
        flexWrap: "wrap",
        gap: 2,
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={(date) => setStartDate(date)}
            disableFuture={true}
            slotProps={{ textField: { size: "small" } }}
          />
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={(date) => setEndDate(date)}
            shouldDisableDate={(date: Dayjs) =>
              startDate !== null && date < startDate
            }
            slotProps={{ textField: { size: "small" } }}
          />
        </LocalizationProvider>
        <Button
          variant="contained"
          onClick={() => fetchReport(startDate, endDate)}
          sx={{
            backgroundColor: PRIMARY,
            "&:hover": { backgroundColor: "#7A1B4D" },
          }}
        >
          Apply
        </Button>
        <Button
          variant="text"
          onClick={() => {
            setStartDate(null);
            setEndDate(null);
            fetchReport(null, null);
          }}
        >
          Reset
        </Button>
      </Box>
      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={() => {
            toPDF();
            axiosInstance.post("/export_log", {
              export_type: "report_pdf",
              record_count: report ? report.categories.length : 0,
              filters: {
                report: "GeneralClient",
                startDate: startDate ? startDate.format("YYYY-MM-DD") : null,
                endDate: endDate ? endDate.format("YYYY-MM-DD") : null,
              },
            });
          }}
        >
          Print
        </Button>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleCsvDownload}
        >
          CSV
        </Button>
      </Box>
    </Paper>
  );

  const pageSx = {
    backgroundColor: "#f6f7f9",
    p: { xs: 2, md: 3 },
    minHeight: "100%",
  };

  if (loading && !report) {
    return (
      <Box
        sx={{
          ...pageSx,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress sx={{ color: PRIMARY }} />
      </Box>
    );
  }

  const summary = report?.summary || {};
  const categories: any[] = report?.categories || [];
  const maxCategoryCount = categories.reduce(
    (max, row) => Math.max(max, row.count),
    0
  );

  return (
    <Box sx={pageSx}>
      {toolbar}
      <Box ref={targetRef}>
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h5"
            sx={{ fontWeight: 800, color: "#2c2c34" }}
          >
            General Client Report
          </Typography>
          <Typography variant="body2" sx={{ color: "#8a8a93", mt: 0.5 }}>
            {rangeLabel}
          </Typography>
        </Box>

        {!report || summary.total_businesses_served === undefined ? (
          <SectionCard>
            <Typography sx={{ color: "#8a8a93" }}>
              No data available for the selected range.
            </Typography>
          </SectionCard>
        ) : (
          <>
            {/* Headline metrics */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                mb: 3,
              }}
            >
              <StatCard
                label="Unique Businesses Served"
                value={summary.total_businesses_served}
                accent={PRIMARY}
              />
              <StatCard
                label="Clients With Multiple Referrals"
                value={summary.clients_multiple_referrals}
                accent="#3F51B5"
              />
              <StatCard
                label="Categories of Assistance"
                value={summary.distinct_categories}
                accent="#00897b"
              />
              <StatCard
                label="Total Referrals"
                value={summary.total_referrals}
                accent="#fdb814"
              />
            </Box>

            {/* Charts */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 3,
                mb: 3,
              }}
            >
              <ChartCard
                title="Categories of Assistance Provided"
                subtitle="Referrals by service area"
                height={Math.max(280, categories.length * 34)}
              >
                <BarChart
                  layout="vertical"
                  data={categories}
                  margin={{ top: 8, right: 48, left: 16, bottom: 8 }}
                >
                  <XAxis type="number" allowDecimals={false} hide />
                  <YAxis
                    type="category"
                    dataKey="category"
                    width={180}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip
                    formatter={(value: any) => [value, "Referrals"]}
                  />
                  <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                    {categories.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]}
                      />
                    ))}
                    <LabelList dataKey="count" position="right" />
                  </Bar>
                </BarChart>
              </ChartCard>

              <ChartCard
                title="Assistance Mix"
                subtitle="Share of referrals by category"
                height={320}
              >
                <PieChart>
                  <Pie
                    data={categories}
                    dataKey="count"
                    nameKey="category"
                    cx="50%"
                    cy="50%"
                    outerRadius={110}
                    label={(entry: any) => entry.category}
                  >
                    {categories.map((entry, index) => (
                      <Cell
                        key={`slice-${index}`}
                        fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: any) => [value, "Referrals"]} />
                </PieChart>
              </ChartCard>
            </Box>

            {/* Category detail table */}
            <SectionCard>
              <SectionTitle subtitle="General categories of assistance provided to clients">
                Assistance Categories Breakdown
              </SectionTitle>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      Referrals
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, width: "40%" }}>
                      Share
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categories.map((row, index) => (
                    <TableRow key={row.category}>
                      <TableCell>{row.category}</TableCell>
                      <TableCell align="right">{row.count}</TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Box sx={{ flexGrow: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={
                                maxCategoryCount > 0
                                  ? (row.count / maxCategoryCount) * 100
                                  : 0
                              }
                              sx={{
                                height: 7,
                                borderRadius: 4,
                                backgroundColor: "#eef0f2",
                                "& .MuiLinearProgress-bar": {
                                  backgroundColor:
                                    CATEGORY_COLORS[
                                      index % CATEGORY_COLORS.length
                                    ],
                                  borderRadius: 4,
                                },
                              }}
                            />
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{ minWidth: 40, textAlign: "right" }}
                          >
                            {Math.round((row.percentage || 0) * 100)}%
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {categories.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} sx={{ color: "#8a8a93" }}>
                        No referrals in the selected range.
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </SectionCard>
          </>
        )}
      </Box>
    </Box>
  );
};

export default GeneralClientReport;
